import ProductAttributeBrandsApiV2 from 'api/v2/admins/product-attribute-brands-api-v2'
import TitlePage from 'components/atoms/title-page'
import GenericModal from 'components/organisms/generic-modal'
import { ALERT_TYPES } from 'constants/alert-constants'
import ProductBrandForm from 'domains/product-brand/organisms/form/product-brand-form'
import ProductBrandTable from 'domains/product-brand/organisms/table/product-brand-table'
import ProductBrandService from 'domains/product-brand/services'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import { Alert, Button, Card, CardHeader } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const defaultProps = {
  pageUtils: {
    setAlertMsg    : () => {},
    setApiErrorMsg : () => {}
  }
}

const ProductBrandPage = ({ pageUtils }) => {
  const [ brands, setBrands ]                       = useState([])
  const [ selectedBrand, setSelectedBrand ]         = useState({})
  const [ isLoading, setIsLoading ]                 = useState(false)
  const [ isCreateModalOpen, setIsCreateModalOpen ] = useState(false)
  const [ isUpdateModalOpen, setIsUpdateModalOpen ] = useState(false)
  const [ tableState, setTableState ]               = useState({})
  const [ pagination, setPagination ]               = useState({})

  const handleFetchBrands = useCallback(async (tableState) => {
    setIsLoading(true)
    await ProductAttributeBrandsApiV2.get({ tableState })
      .then((response) => {
        setBrands(response.data.product_brands)
        setPagination(response.data.meta)
        setTableState(tableState)
      })
      .catch(() => {
        pageUtils.setAlertMsg('Unable to fetch brand', ALERT_TYPES.DANGER)
      })
      .finally(() => setIsLoading(false))
  }, [ pageUtils ])

  // func to create new product brand
  const handleCreateBrand = async (values, formikBag) => {
    formikBag.setSubmitting(true)
    const classificationPayload = ProductBrandService.prepClassification(values.classifications)

    const params = {
      'label'              : values.label,
      'url'                : values.url,
      'classification_ids' : classificationPayload
    }

    await ProductAttributeBrandsApiV2.create(params)
      .then(() => {
        pageUtils.setAlertMsg('Product brand has been created', ALERT_TYPES.SUCCESS)
        toggleCreateModal()
        handleFetchBrands(tableState)
        formikBag.resetForm()
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        formikBag.setErrors(error.response.data.messages)
      })
  }

  // func to update product brand
  const handleUpdateBrand = async (values, formikBag) => {
    const classificationPayload = ProductBrandService.prepClassification(values.classifications)

    const params = {
      'label'              : values.label,
      'url'                : values.url,
      'is_searchable'      : values.isSearchable,
      'classification_ids' : classificationPayload
    }

    formikBag.setSubmitting(true)
    await ProductAttributeBrandsApiV2.update(selectedBrand.id, params)
      .then(() => {
        pageUtils.setAlertMsg(`Product brand #${selectedBrand.id} has been updated`, ALERT_TYPES.SUCCESS)
        toggleUpdateModal()
        setSelectedBrand({})
        handleFetchBrands(tableState)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        formikBag.setErrors(error.response.data.messages)
      })
      .finally(() => formikBag.setSubmitting(false))
  }

  // func to change product brand status to `enabled`
  const handleEnableBrand = useCallback((datum) => {
    ProductAttributeBrandsApiV2.enable(datum.id)
      .then((response) => {
        const brand = response.data.brand
        pageUtils.setAlertMsg(`Product brand ${brand.label} has been enabled`, ALERT_TYPES.SUCCESS)
        handleFetchBrands(tableState)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
      })
  }, [ pageUtils, handleFetchBrands, tableState ])

  const handleDisableBrand = useCallback((datum) => {
    ProductAttributeBrandsApiV2.disable(datum.id)
      .then((response) => {
        const brand = response.data.brand
        pageUtils.setAlertMsg(`Product brand ${brand.label} has been disabled`, ALERT_TYPES.SUCCESS)
        handleFetchBrands(tableState)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
      })
  }, [ pageUtils, handleFetchBrands, tableState ])

  const handleOpenUpdateModal = useCallback((datum) => {
    setSelectedBrand(datum)
    setIsUpdateModalOpen(true)
  }, [ setIsUpdateModalOpen, setSelectedBrand ])

  const toggleCreateModal = () => setIsCreateModalOpen(!isCreateModalOpen)
  const toggleUpdateModal = () => setIsUpdateModalOpen(!isUpdateModalOpen)

  return (
    <>
      <TitlePage mainTitle='Product Brands List' />

      <Alert color='primary'>
        <p>
          <i className='fas fa-info-circle mr-3' />
          { 'CATATAN:' }
        </p>
        <p>
          { 'Brand Classification adalah pengelompokan Brand berdasarkan ' }
          { 'klasifikasi produk yang telah ditentukan oleh Tinkerlust.' }
        </p>
        <p>{ 'Suatu produk bisa memiliki lebih dari 1 Brand Classification' }</p>
      </Alert>
      <Alert color='secondary'>
        <p>
          <i className='fas fa-info-circle mr-3' />
          { 'Pada halaman ini, user dapat menambahkan menambahkan, ' }
          { 'meng-update (edit), serta mengubah status (enable/disable) Brand.' }
        </p>
        <p>{ 'Tutorial dapat dilihat dari URL berikut' }</p>
        <p>
          <a href='https://drive.google.com/file/d/1e_dr70W0Unw4-8Xxg3KRZD5_m5AqWnFy/view?usp=sharing'>
            { 'Tambah Brand' }
          </a>
        </p>
        <p>
          <a href='https://drive.google.com/file/d/1Usum8uSSGvy7CMIi9XzyVSjXPwAzzkc7/view?usp=sharing'>
            { 'Update / Edit Brand' }
          </a>
        </p>
        <p>
          <a href='https://drive.google.com/file/d/1ugCICpNpcOtqaA4HHkAkxV3gODFJqZOA/view?usp=sharing'>
            { 'Ubah Status (Enable/Disable) Brand' }
          </a>
        </p>
      </Alert>

      <Card>
        <CardHeader>
          <Button
            className='float-right'
            color='primary'
            onClick={ toggleCreateModal }
          >
            { 'Tambah Brand Baru' }
          </Button>
        </CardHeader>

        <ProductBrandTable
          data={ brands }
          isLoading={ isLoading }
          pagination={ pagination }
          onFetchData={ handleFetchBrands }
          rowButtonProps={{
            handleOpenUpdateModal : handleOpenUpdateModal,
            handleDisableBrand    : handleDisableBrand,
            handleEnableBrand     : handleEnableBrand
          }}
        />

      </Card>
      { /* Create Brand Modal Form */ }
      <GenericModal
        isOpen={ isCreateModalOpen }
        toggle={ toggleCreateModal }
        title={ 'Create new Brand' }
        size='lg'
      >
        <ProductBrandForm
          handleSubmit={ handleCreateBrand }
        />
      </GenericModal>
      { /* Update Brand Modal Form */ }
      <GenericModal
        isOpen={ isUpdateModalOpen }
        toggle={ toggleUpdateModal }
        title={ 'Update Existing Brand' }
        size='lg'
      >
        <ProductBrandForm
          handleSubmit={ handleUpdateBrand }
          productBrand={ selectedBrand }
        />
      </GenericModal>
    </>
  )
}
ProductBrandPage.propTypes    = propTypes
ProductBrandPage.defaultProps = defaultProps

export default ProductBrandPage
